import { useState, useEffect } from 'react';
import { 
  Mail, Smartphone, FileText, Plus, Save, Trash2, Copy, Eye, EyeOff,
  ChevronDown, Code, Type, Image, Link2, Variable, Undo, Redo,
  Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlertTriangle,
  CheckCircle, Building2, Clock, Hash, MapPin, User, Calendar
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';

// Template categories
const TEMPLATE_CATEGORIES = [
  { id: 'violation', label: 'Violations', icon: AlertTriangle, color: 'rose' },
  { id: 'application', label: 'Applications', icon: FileText, color: 'indigo' },
  { id: 'inspection', label: 'Inspections', icon: CheckCircle, color: 'emerald' },
  { id: 'status', label: 'Status Changes', icon: Clock, color: 'amber' },
  { id: 'general', label: 'General', icon: Mail, color: 'zinc' },
];

// Available variables for templates
const TEMPLATE_VARIABLES = [
  { id: 'property_address', label: 'Property Address', icon: MapPin, example: '350 Fifth Avenue, New York, NY' },
  { id: 'property_block', label: 'Block Number', icon: Hash, example: '847' },
  { id: 'property_lot', label: 'Lot Number', icon: Hash, example: '1' },
  { id: 'property_bbl', label: 'BBL', icon: Building2, example: '1008470001' },
  { id: 'violation_number', label: 'Violation Number', icon: AlertTriangle, example: 'V-2025-001234' },
  { id: 'violation_type', label: 'Violation Type', icon: AlertTriangle, example: 'DOB ECB' },
  { id: 'violation_description', label: 'Violation Description', icon: FileText, example: 'Failure to maintain building...' },
  { id: 'violation_status', label: 'Violation Status', icon: CheckCircle, example: 'Open' },
  { id: 'violation_date', label: 'Violation Date', icon: Calendar, example: 'January 9, 2026' },
  { id: 'application_number', label: 'Application Number', icon: Hash, example: '123456789' },
  { id: 'application_type', label: 'Application Type', icon: FileText, example: 'Alteration Type 1' },
  { id: 'application_status', label: 'Application Status', icon: CheckCircle, example: 'Approved' },
  { id: 'inspection_date', label: 'Inspection Date', icon: Calendar, example: 'January 15, 2026' },
  { id: 'inspection_type', label: 'Inspection Type', icon: CheckCircle, example: 'Final Inspection' },
  { id: 'user_name', label: 'User Name', icon: User, example: 'John Smith' },
  { id: 'user_email', label: 'User Email', icon: Mail, example: 'john@example.com' },
  { id: 'company_name', label: 'Company Name', icon: Building2, example: 'Insite360' },
  { id: 'current_date', label: 'Current Date', icon: Calendar, example: 'January 9, 2026' },
  { id: 'dashboard_link', label: 'Dashboard Link', icon: Link2, example: 'https://app.insite360.com/dashboard' },
];

// Default templates
const DEFAULT_TEMPLATES = [
  {
    id: 'new_violation_email',
    name: 'New Violation Alert',
    category: 'violation',
    type: 'email',
    subject: 'New Violation Filed - {{property_address}}',
    body: `<h2>New Violation Alert</h2>
<p>A new violation has been filed for a property you're monitoring:</p>

<table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Property</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee; font-weight: bold;">{{property_address}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Block/Lot</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee;">{{property_block}} / {{property_lot}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Violation #</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee;">{{violation_number}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Type</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee;">{{violation_type}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Description</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee;">{{violation_description}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; color: #666;">Filed Date</td>
    <td style="padding: 10px;">{{violation_date}}</td>
  </tr>
</table>

<p><a href="{{dashboard_link}}" style="display: inline-block; padding: 12px 24px; background: #6366f1; color: white; text-decoration: none; border-radius: 6px;">View in Dashboard</a></p>

<p style="color: #666; font-size: 12px; margin-top: 30px;">You received this alert because you're monitoring this property on {{company_name}}.</p>`,
    isDefault: true
  },
  {
    id: 'new_violation_sms',
    name: 'New Violation SMS',
    category: 'violation',
    type: 'sms',
    body: `⚠️ NEW VIOLATION: {{property_address}} - {{violation_type}} filed on {{violation_date}}. View details: {{dashboard_link}}`,
    isDefault: true
  },
  {
    id: 'status_change_email',
    name: 'Status Change Alert',
    category: 'status',
    type: 'email',
    subject: 'Status Update - {{property_address}}',
    body: `<h2>Status Change Alert</h2>
<p>The status of a violation you're monitoring has changed:</p>

<div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
  <p style="margin: 0 0 10px;"><strong>Property:</strong> {{property_address}}</p>
  <p style="margin: 0 0 10px;"><strong>Violation:</strong> {{violation_number}}</p>
  <p style="margin: 0;"><strong>New Status:</strong> <span style="color: #10b981; font-weight: bold;">{{violation_status}}</span></p>
</div>

<p><a href="{{dashboard_link}}" style="display: inline-block; padding: 12px 24px; background: #6366f1; color: white; text-decoration: none; border-radius: 6px;">View Details</a></p>`,
    isDefault: true
  },
  {
    id: 'application_filed_email',
    name: 'New Application Filed',
    category: 'application',
    type: 'email',
    subject: 'New Application - {{property_address}}',
    body: `<h2>New Application Alert</h2>
<p>A new permit application has been filed for a property you're monitoring:</p>

<table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Property</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee; font-weight: bold;">{{property_address}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Application #</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee;">{{application_number}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; border-bottom: 1px solid #eee; color: #666;">Type</td>
    <td style="padding: 10px; border-bottom: 1px solid #eee;">{{application_type}}</td>
  </tr>
  <tr>
    <td style="padding: 10px; color: #666;">Status</td>
    <td style="padding: 10px;">{{application_status}}</td>
  </tr>
</table>

<p><a href="{{dashboard_link}}" style="display: inline-block; padding: 12px 24px; background: #6366f1; color: white; text-decoration: none; border-radius: 6px;">View in Dashboard</a></p>`,
    isDefault: true
  },
  {
    id: 'inspection_scheduled_email',
    name: 'Inspection Scheduled',
    category: 'inspection',
    type: 'email',
    subject: 'Inspection Scheduled - {{property_address}}',
    body: `<h2>Inspection Scheduled</h2>
<p>An inspection has been scheduled for a property you're monitoring:</p>

<div style="background: #ecfdf5; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #10b981;">
  <p style="margin: 0 0 10px;"><strong>Property:</strong> {{property_address}}</p>
  <p style="margin: 0 0 10px;"><strong>Inspection Type:</strong> {{inspection_type}}</p>
  <p style="margin: 0;"><strong>Scheduled Date:</strong> {{inspection_date}}</p>
</div>

<p><a href="{{dashboard_link}}" style="display: inline-block; padding: 12px 24px; background: #6366f1; color: white; text-decoration: none; border-radius: 6px;">View Details</a></p>`,
    isDefault: true
  },
  {
    id: 'daily_digest_email',
    name: 'Daily Digest',
    category: 'general',
    type: 'email',
    subject: 'Daily Compliance Report - {{current_date}}',
    body: `<h2>Daily Compliance Report</h2>
<p>Here's your daily summary for {{current_date}}:</p>

<p>This is a summary of all monitoring activity across your watchlists.</p>

<p><a href="{{dashboard_link}}" style="display: inline-block; padding: 12px 24px; background: #6366f1; color: white; text-decoration: none; border-radius: 6px;">View Full Report</a></p>

<p style="color: #666; font-size: 12px; margin-top: 30px;">Sent by {{company_name}}</p>`,
    isDefault: true
  }
];

export default function TemplateBuilder({ onSave, initialTemplates = [], isWhiteLabel = false }) {
  const [templates, setTemplates] = useState([...DEFAULT_TEMPLATES, ...initialTemplates]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // New template state
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'violation',
    type: 'email',
    subject: '',
    body: ''
  });

  // Editing state
  const [editingTemplate, setEditingTemplate] = useState(null);

  const filteredTemplates = templates.filter(t => {
    if (filterCategory !== 'all' && t.category !== filterCategory) return false;
    if (filterType !== 'all' && t.type !== filterType) return false;
    return true;
  });

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditingTemplate({ ...template });
    setEditMode(false);
    setPreviewMode(false);
  };

  const handleSaveTemplate = () => {
    if (!editingTemplate.name) {
      toast.error('Template name is required');
      return;
    }
    
    const updatedTemplates = templates.map(t => 
      t.id === editingTemplate.id ? editingTemplate : t
    );
    setTemplates(updatedTemplates);
    setSelectedTemplate(editingTemplate);
    setEditMode(false);
    toast.success('Template saved');
    
    if (onSave) {
      onSave(updatedTemplates);
    }
  };

  const handleCreateTemplate = () => {
    if (!newTemplate.name) {
      toast.error('Template name is required');
      return;
    }
    
    const template = {
      ...newTemplate,
      id: `custom_${Date.now()}`,
      isDefault: false
    };
    
    setTemplates([...templates, template]);
    setCreateDialogOpen(false);
    setNewTemplate({ name: '', category: 'violation', type: 'email', subject: '', body: '' });
    setSelectedTemplate(template);
    setEditingTemplate({ ...template });
    setEditMode(true);
    toast.success('Template created');
  };

  const handleDuplicateTemplate = (template) => {
    const duplicate = {
      ...template,
      id: `${template.id}_copy_${Date.now()}`,
      name: `${template.name} (Copy)`,
      isDefault: false
    };
    setTemplates([...templates, duplicate]);
    toast.success('Template duplicated');
  };

  const handleDeleteTemplate = (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    const template = templates.find(t => t.id === templateId);
    if (template?.isDefault) {
      toast.error('Cannot delete default templates');
      return;
    }
    
    setTemplates(templates.filter(t => t.id !== templateId));
    if (selectedTemplate?.id === templateId) {
      setSelectedTemplate(null);
      setEditingTemplate(null);
    }
    toast.success('Template deleted');
  };

  const insertVariable = (variableId) => {
    if (!editingTemplate) return;
    
    const variable = `{{${variableId}}}`;
    // Insert at cursor or append
    setEditingTemplate(prev => ({
      ...prev,
      body: prev.body + variable
    }));
    setShowVariables(false);
    toast.success(`Inserted ${variableId}`);
  };

  // Render preview with sample data
  const renderPreview = (template) => {
    if (!template) return '';
    
    let preview = template.body;
    TEMPLATE_VARIABLES.forEach(v => {
      const regex = new RegExp(`{{${v.id}}}`, 'g');
      preview = preview.replace(regex, `<span class="text-indigo-400 bg-indigo-500/10 px-1 rounded">${v.example}</span>`);
    });
    return preview;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-400" />
            {isWhiteLabel ? 'Branded Email Templates' : 'Communication Templates'}
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            {isWhiteLabel 
              ? 'Customize email templates with your branding'
              : 'Manage templates for alerts and notifications'
            }
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary" data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-40 input-dark">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {TEMPLATE_CATEGORIES.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-32 input-dark">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
          </SelectContent>
        </Select>
        
        <span className="text-sm text-zinc-500 ml-auto">
          {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto pr-2">
          {filteredTemplates.map(template => {
            const category = TEMPLATE_CATEGORIES.find(c => c.id === template.category);
            const CategoryIcon = category?.icon || Mail;
            const isSelected = selectedTemplate?.id === template.id;
            
            return (
              <button
                key={template.id}
                onClick={() => handleSelectTemplate(template)}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  isSelected 
                    ? 'border-indigo-500 bg-indigo-500/10' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                data-testid={`template-${template.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg bg-${category?.color || 'zinc'}-500/20`}>
                    {template.type === 'email' ? (
                      <Mail className={`w-4 h-4 text-${category?.color || 'zinc'}-400`} />
                    ) : (
                      <Smartphone className={`w-4 h-4 text-${category?.color || 'zinc'}-400`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white truncate">{template.name}</p>
                      {template.isDefault && (
                        <span className="text-[10px] bg-zinc-700 text-zinc-400 px-1.5 py-0.5 rounded">DEFAULT</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5 capitalize">{template.type} • {template.category}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Template Editor/Preview */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <div className="glass-card rounded-xl border border-white/10 overflow-hidden">
              {/* Editor Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10 bg-zinc-900/50">
                <div className="flex items-center gap-3">
                  {editingTemplate?.type === 'email' ? (
                    <Mail className="w-5 h-5 text-indigo-400" />
                  ) : (
                    <Smartphone className="w-5 h-5 text-emerald-400" />
                  )}
                  <div>
                    <h4 className="font-medium text-white">{editingTemplate?.name}</h4>
                    <p className="text-xs text-zinc-500 capitalize">{editingTemplate?.type} template</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewMode(!previewMode)}
                    className={previewMode ? 'bg-white/10' : ''}
                  >
                    {previewMode ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                    {previewMode ? 'Edit' : 'Preview'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicateTemplate(selectedTemplate)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  {!selectedTemplate.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                      className="text-rose-400 hover:text-rose-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Editor Body */}
              <div className="p-4 space-y-4">
                {previewMode ? (
                  /* Preview Mode */
                  <div className="space-y-4">
                    {editingTemplate?.type === 'email' && editingTemplate?.subject && (
                      <div>
                        <Label className="text-xs text-zinc-500">Subject</Label>
                        <p className="text-white mt-1" dangerouslySetInnerHTML={{ 
                          __html: editingTemplate.subject.replace(/{{(\w+)}}/g, (_, v) => {
                            const variable = TEMPLATE_VARIABLES.find(x => x.id === v);
                            return `<span class="text-indigo-400 bg-indigo-500/10 px-1 rounded">${variable?.example || v}</span>`;
                          })
                        }} />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-zinc-500">Body Preview</Label>
                      <div 
                        className="mt-2 p-4 bg-white rounded-lg text-zinc-900 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: renderPreview(editingTemplate) }}
                      />
                    </div>
                  </div>
                ) : (
                  /* Edit Mode */
                  <div className="space-y-4">
                    <div>
                      <Label className="text-zinc-300">Template Name</Label>
                      <Input
                        value={editingTemplate?.name || ''}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                        className="input-dark mt-1"
                        disabled={selectedTemplate?.isDefault}
                      />
                    </div>
                    
                    {editingTemplate?.type === 'email' && (
                      <div>
                        <Label className="text-zinc-300">Subject Line</Label>
                        <Input
                          value={editingTemplate?.subject || ''}
                          onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                          className="input-dark mt-1"
                          placeholder="Email subject..."
                        />
                      </div>
                    )}
                    
                    {/* Variable Insertion */}
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-300">
                        {editingTemplate?.type === 'email' ? 'Email Body (HTML)' : 'SMS Body'}
                      </Label>
                      <Popover open={showVariables} onOpenChange={setShowVariables}>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-indigo-400 hover:text-indigo-300">
                            <Variable className="w-4 h-4 mr-1" />
                            Insert Variable
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0 bg-zinc-900 border-white/10" align="end">
                          <div className="p-2 border-b border-white/10">
                            <p className="text-xs text-zinc-400">Click a variable to insert it</p>
                          </div>
                          <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                            {TEMPLATE_VARIABLES.map(v => {
                              const VarIcon = v.icon;
                              return (
                                <button
                                  key={v.id}
                                  onClick={() => insertVariable(v.id)}
                                  className="w-full text-left p-2 rounded-lg hover:bg-white/10 flex items-center gap-2 transition-colors"
                                >
                                  <VarIcon className="w-4 h-4 text-zinc-500" />
                                  <div className="flex-1">
                                    <p className="text-sm text-white">{v.label}</p>
                                    <p className="text-xs text-zinc-500 font-mono">{`{{${v.id}}}`}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <textarea
                      value={editingTemplate?.body || ''}
                      onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                      className="w-full h-64 p-4 bg-zinc-950 border border-white/10 rounded-lg text-white font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      placeholder={editingTemplate?.type === 'email' 
                        ? '<h2>Your email content...</h2>\n<p>Use {{variable_name}} for dynamic content.</p>'
                        : 'Your SMS message... Use {{variable_name}} for dynamic content.'
                      }
                    />
                    
                    {editingTemplate?.type === 'sms' && (
                      <p className="text-xs text-zinc-500">
                        Character count: {editingTemplate?.body?.length || 0} / 160 
                        {(editingTemplate?.body?.length || 0) > 160 && (
                          <span className="text-amber-400 ml-2">
                            (Will be sent as {Math.ceil((editingTemplate?.body?.length || 0) / 160)} messages)
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Editor Footer */}
              {!previewMode && (
                <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10 bg-zinc-900/50">
                  <Button
                    variant="ghost"
                    onClick={() => setEditingTemplate({ ...selectedTemplate })}
                    className="btn-ghost"
                  >
                    <Undo className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                  <Button
                    onClick={handleSaveTemplate}
                    className="btn-primary"
                    data-testid="save-template-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* No Template Selected */
            <div className="glass-card rounded-xl p-12 text-center border border-white/10">
              <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-white mb-2">Select a Template</h4>
              <p className="text-zinc-400 mb-4">Choose a template from the list to edit or preview</p>
              <Button onClick={() => setCreateDialogOpen(true)} variant="outline" className="btn-secondary">
                <Plus className="w-4 h-4 mr-2" />
                Create New Template
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create Template Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="glass-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Template</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Template Name</Label>
              <Input
                placeholder="My Custom Template"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                className="input-dark mt-1"
                data-testid="new-template-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Category</Label>
                <Select 
                  value={newTemplate.category}
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Type</Label>
                <Select 
                  value={newTemplate.type}
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, type: v }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setCreateDialogOpen(false)} className="btn-ghost">
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate} className="btn-primary" data-testid="confirm-create-template">
              <Plus className="w-4 h-4 mr-2" />
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
